import React from 'react';
import { SlideWrapper, GlassCard, SectionLabel } from './SharedComponents';

export const ImpactFlywheelDetailedSlide = ({ slide }) => {
  const stepColors = ['#22c55e', '#0068ff', '#f59e0b', '#8b5cf6', '#06b6d4'];
  const stepIcons = [
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" /></svg>,
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V7m0 10v1m9-9a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
  ];

  const angles = [270, 342, 54, 126, 198];
  const radius = 155;
  const cx = 200, cy = 200;

  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#060610] to-[#030308]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-[#0068ff] opacity-[0.06] blur-[200px] rounded-full" />
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#22c55e] opacity-[0.03] blur-[150px] rounded-full" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#f59e0b] opacity-[0.03] blur-[120px] rounded-full" />
      </div>
    }>
      <div className="relative z-10 flex items-center justify-center h-full px-6 py-4">
        {/* Left: Info */}
        <div className="flex-1 max-w-[380px] mr-8">
          <SectionLabel delay={0}>Self-Reinforcing Growth</SectionLabel>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mt-4 mb-2 animate-slide-up">{slide.title}</h2>
          <p className="text-white/35 text-sm mb-6 animate-fade-in" style={{ animationDelay: '0.05s' }}>{slide.subtitle}</p>

          <div className="space-y-2.5 mb-6">
            {slide.steps.map((step, idx) => (
              <GlassCard key={idx} delay={0.15 + idx * 0.07} className="p-3.5 flex items-start gap-3" style={{
                borderColor: `${stepColors[idx]}20`, borderLeftWidth: '3px', borderLeftColor: stepColors[idx]
              }}>
                <div className="w-7 h-7 rounded-lg shrink-0 flex items-center justify-center" style={{ background: `${stepColors[idx]}15`, color: stepColors[idx] }}>{stepIcons[idx]}</div>
                <div>
                  <div className="text-sm font-bold mb-0.5" style={{ color: stepColors[idx] }}>{step.name}</div>
                  <p className="text-white/40 text-[11px] leading-relaxed">{step.description}</p>
                  {step.items.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {step.items.map((item, i) => (
                        <span key={i} className="text-[9px] px-2 py-0.5 rounded-full" style={{ color: stepColors[idx], background: `${stepColors[idx]}10`, border: `1px solid ${stepColors[idx]}15` }}>{item}</span>
                      ))}
                    </div>
                  )}
                </div>
              </GlassCard>
            ))}
          </div>

          <GlassCard delay={0.6} className="px-5 py-2.5 text-center" style={{ borderColor: 'rgba(0,104,255,0.15)' }}>
            <span className="text-white/40 text-[11px]">{slide.closing[0]}</span><br />
            <span className="text-[#0068ff] text-[11px] font-semibold">{slide.closing[1]}</span>
          </GlassCard>
        </div>

        {/* Right: Flywheel Diagram */}
        <div className="relative w-[400px] h-[400px] shrink-0 animate-scale-in" style={{ animationDelay: '0.2s' }}>
          {/* Rotating dashed ring */}
          <svg className="absolute inset-0 w-full h-full animate-[spin_30s_linear_infinite]" viewBox="0 0 400 400">
            <circle cx={cx} cy={cy} r={radius + 15} fill="none" stroke="#0068ff" strokeWidth="1" strokeOpacity="0.12" strokeDasharray="10 6" />
          </svg>
          {/* Inner ring */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 400">
            <circle cx={cx} cy={cy} r={radius - 40} fill="none" stroke="#0068ff" strokeWidth="0.5" strokeOpacity="0.08" />
          </svg>

          {/* Center hub */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full flex items-center justify-center z-10" style={{ background: '#0068ff12', border: '2px solid #0068ff35' }}>
            <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#0068ff]" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          </div>

          {/* Nodes */}
          {slide.steps.map((step, idx) => {
            const rad = angles[idx] * (Math.PI / 180);
            const x = cx + radius * Math.cos(rad);
            const y = cy + radius * Math.sin(rad);
            const color = stepColors[idx];
            return (
              <div key={idx} className="absolute animate-slide-up" style={{
                left: x - 44, top: y - 28, width: 88,
                animationDelay: `${0.3 + idx * 0.08}s`
              }}>
                <div className="rounded-xl p-2 text-center transition-transform duration-300 hover:scale-110" style={{
                  background: `linear-gradient(135deg, ${color}15, ${color}08)`,
                  border: `1px solid ${color}35`,
                  boxShadow: `0 0 20px ${color}10`
                }}>
                  <div className="w-6 h-6 rounded-md mx-auto mb-1 flex items-center justify-center" style={{ background: `${color}20`, color }}>{stepIcons[idx]}</div>
                  <span className="text-white text-[10px] font-bold leading-tight block">{step.name}</span>
                </div>
              </div>
            );
          })}

          {/* Arrows between nodes */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 400">
            <defs>
              {stepColors.map((c, i) => (
                <marker key={i} id={`fwD${i}`} markerWidth="6" markerHeight="5" refX="5" refY="2.5" orient="auto">
                  <polygon points="0 0,6 2.5,0 5" fill={c} opacity="0.6" />
                </marker>
              ))}
            </defs>
            {angles.map((a, i) => {
              const next = angles[(i + 1) % 5];
              const r = radius - 5;
              const startDeg = a + 18;
              const endDeg = next < a ? next + 360 - 18 : next - 18;
              const startRad = startDeg * (Math.PI / 180);
              const endRad = (endDeg % 360) * (Math.PI / 180);
              const largeArc = (endDeg - startDeg + 360) % 360 > 180 ? 1 : 0;
              return (
                <path key={i}
                  d={`M ${cx + r * Math.cos(startRad)} ${cy + r * Math.sin(startRad)} A ${r} ${r} 0 ${largeArc} 1 ${cx + r * Math.cos(endRad)} ${cy + r * Math.sin(endRad)}`}
                  fill="none" stroke={stepColors[(i + 1) % 5]} strokeWidth="1.5" strokeOpacity="0.35"
                  markerEnd={`url(#fwD${(i + 1) % 5})`}
                />
              );
            })}
          </svg>
        </div>
      </div>
    </SlideWrapper>
  );
};
